import {
    useEffect,
    useState,
    useRef,
    type FC
} from "react";
import {
    View
} from "react-native";
import type ICheckBoxProps from "./type";
import stylesheet, {
    getCheckBoxCheckedState,
    CHECK_BOX_ARIA_CHECKED,
    getCheckBoxStateStyle,
    getCheckBoxMeasures,
    getCheckBoxColors,
    getCheckBoxType,
    useStyles
} from "./stylesheet";
import CheckBoxMark from "./components/mark";
import {
    NCoreUIKitTheme
} from "../../core/hooks";
import {
    useReducedMotion
} from "../../helpers/motion";
import type {
    SelectionControlIndicatorStateType
} from "../selectionControl/type";
import {
    warnOnce
} from "../../utils/development";
import SelectionControl from "../selectionControl";
import StateLayer from "../stateLayer";
import Loading from "../loading";

const CheckBox: FC<ICheckBoxProps> = ({
    displayBehaviourWhileLoading,
    isDefaultChecked = false,
    isIndeterminate = false,
    isDisabled = false,
    isLoading = false,
    type = "primary",
    isError = false,
    onCheckedChange,
    spreadBehaviour,
    customTheme,
    isChecked,
    onPress,
    ...props
}) => {
    const {
        shapeConfigs,
        shadowAlpha,
        elevations,
        durations,
        iconSizes,
        radiuses,
        borders,
        colors,
        spaces
    } = NCoreUIKitTheme.useContext(customTheme);

    const isReduceMotionEnabled = useReducedMotion();

    const [
        innerChecked,
        setInnerChecked
    ] = useState(isDefaultChecked);

    const isControlled = isChecked !== undefined;

    const checkedState = getCheckBoxCheckedState({
        isChecked: isControlled ? isChecked : innerChecked,
        isIndeterminate
    });

    const handlePress = () => {
        const nextChecked = checkedState !== "checked";

        if(!isControlled) {
            setInnerChecked(nextChecked);
        }

        onCheckedChange?.(nextChecked);

        onPress?.();
    };

    const currentOnPress = !isControlled || !!onCheckedChange || !!onPress ? handlePress : undefined;

    const previousCheckedStateRef = useRef(checkedState);

    const isMarkAnimated = previousCheckedStateRef.current !== checkedState;

    const currentType = getCheckBoxType({
        colors,
        type
    });

    const currentMeasures = getCheckBoxMeasures({
        iconSizes,
        radiuses,
        borders,
        spaces
    });

    const currentColors = getCheckBoxColors({
        isSelected: checkedState !== "unchecked",
        currentType,
        isDisabled,
        isError,
        colors
    });

    const {
        glyphContainer: glyphContainerDynamicStyle,
        box: boxDynamicStyle
    } = useStyles({
        currentMeasures,
        currentColors,
        shapeConfigs,
        shadowAlpha,
        elevations,
        isDisabled,
        colors
    });

    useEffect(() => {
        previousCheckedStateRef.current = checkedState;
    });

    useEffect(() => {
        if(typeof isChecked !== "string") {
            return;
        }

        warnOnce({
            message: "CheckBox: the string values of \"isChecked\" (\"checked\", \"partially\") are deprecated and will be removed in the next major version. Use a boolean \"isChecked\" and \"isIndeterminate\" instead."
        });
    }, [
        isChecked
    ]);

    useEffect(() => {
        if(displayBehaviourWhileLoading === undefined) {
            return;
        }

        warnOnce({
            message: "CheckBox: \"displayBehaviourWhileLoading\" is deprecated and will be removed in the next major version. A loading CheckBox keeps its type colour and always blocks presses."
        });
    }, [
        displayBehaviourWhileLoading
    ]);

    const renderGlyph = () => {
        if(checkedState === "unchecked") {
            return null;
        }

        return <CheckBoxMark
            glyphStrokeWidth={shapeConfigs.glyphStrokeWidth}
            glyphColor={currentColors.glyphColor}
            size={currentMeasures.glyphSize}
            isAnimated={isMarkAnimated}
            checkedState={checkedState}
            customTheme={customTheme}
            key={checkedState}
        />;
    };

    const renderIndicator = ({
        isFocusVisible,
        isHovered,
        isPressed
    }: SelectionControlIndicatorStateType) => {
        const {
            loading: loadingStateStyle,
            box: boxStateStyle
        } = getCheckBoxStateStyle({
            isReduceMotionEnabled,
            currentMeasures,
            isFocusVisible,
            currentColors,
            shapeConfigs,
            durations,
            isHovered,
            isPressed,
            borders,
            colors,
            type
        });

        if(isLoading) {
            return <Loading
                customColor={currentColors.spinnerColor}
                size={currentMeasures.boxSize}
                customTheme={customTheme}
                style={loadingStateStyle}
            />;
        }

        return <View
            style={[
                stylesheet.box,
                boxDynamicStyle,
                boxStateStyle
            ]}
        >
            <StateLayer
                borderRadius={currentMeasures.borderRadius}
                borderWidth={currentMeasures.borderWidth}
                overlayColor={currentColors.overlayColor}
                customTheme={customTheme}
                isHovered={isHovered}
                isPressed={isPressed}
            />
            <View
                style={[
                    stylesheet.glyphContainer,
                    glyphContainerDynamicStyle
                ]}
            >
                {renderGlyph()}
            </View>
        </View>;
    };

    return <SelectionControl
        {...props}
        checkedState={CHECK_BOX_ARIA_CHECKED[checkedState]}
        renderIndicator={renderIndicator}
        spreadBehaviour={spreadBehaviour}
        customTheme={customTheme}
        onPress={currentOnPress}
        isDisabled={isDisabled}
        isLoading={isLoading}
        isError={isError}
        role="checkbox"
    />;
};
export default CheckBox;
